import { useCallback } from 'react';
import { type RoutePath, type RouteSegments, useNavigationContainerRef } from 'expo-router';
import { Platform } from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a type-safe `reset` navigation function for the Expo Router setup.
 */
export const useNavigationReset = () => {
  const navigationRef = useNavigationContainerRef();

  return useCallback(
    (state: ResetState) => {
      if (!navigationRef.isReady()) return;

      if (Platform.OS === 'web') {
        navigationRef.reset(state as never);
        return;
      }

      // NOTE: We defer on native to prevent conflicts between
      //       multiple modals.
      requestIdleCallback(() => {
        setTimeout(() => {
          navigationRef.reset(state as never);
        });
      });
    },
    [navigationRef],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetState = {
  index?: number;
  routes: ResetRoute[];
};

type ResetRoute = {
  name: RootLevelRouteName;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

// NOTE: Nested-navigator screen names are local to their parent stack, so they
//       can't be statically narrowed from the app-wide `RoutePath` union.
type ResetNestedRoute = {
  name: string;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

type RootLevelRouteName = RouteSegments<RoutePath>[number];
